/**
 * REGUŁY POWIADOMIEŃ — katalog zdarzeń, które wysyłają e-mail, i rozstrzyganie,
 * czy dana organizacja chce dany mail dostać.
 *
 * Dlaczego to powstało (01.11.2026): właściciel dostawał mail „Spóźnienie:
 * 0 min" przy pierwszym tyknięciu po terminie każdego zadania z minutnika,
 * a organizacja nie miała żadnego miejsca, w którym mogłaby powiedzieć „wyślij
 * dopiero od kwadransa". Plan i pomiary:
 * `docs/16-ZAMOWIENIA/realizacja/F0-reguly-powiadomien-organow.md`.
 *
 * Jedno źródło dla dwóch czytelników:
 *  - `neurosense-core` (`PocztaZdarzen`, podsumowanie dobowe) — WYMUSZA regułę
 *    przed wysyłką,
 *  - `universe-shell` (Ustawienia → Powiadomienia) — POKAZUJE ją i zapisuje.
 * Ekran i nadawca liczą skutek tą samą funkcją `regulaEfektywna()`, więc nie
 * da się pokazać trybu, którego nadawca nie wykona.
 *
 * Każdy parametr ma mechanizm i test (zasada bez ślepaków):
 *  - `wlaczone`            → `PocztaZdarzen.obsluz()` / `sendDailySummaries()`,
 *  - `odMinutSpoznienia`   → porównanie z `minutes_overdue` z ładunku Agendy,
 *  - `tylkoKrytyczne`      → porównanie z `alert_type` z ładunku NeuroSense,
 *  - `powtorzCoMin`        → okno tłumienia zamiast stałej z `mapa.ts`.
 * Pilnuje tego `tests/unit/services/regulyPowiadomien.test.ts`.
 */

/** Tryby gotowe do wyboru — jeden przełącznik na organ. */
export const TRYBY_POWIADOMIEN = ['natychmiast', 'zrownowazony', 'tylko_wazne', 'wlasny'] as const;
export type TrybPowiadomien = (typeof TRYBY_POWIADOMIEN)[number];

/**
 * Tryb organizacji, która niczego nie wybrała — „Zrównoważony" (decyzja
 * właściciela z 01.11.2026: „ustaw domyślny tryb Zrównoważony dla wszystkich").
 * Zmienia zachowanie względem stanu sprzed 889 wyłącznie w jednym miejscu:
 * zadanie po terminie wysyła mail dopiero od 15 minut spóźnienia. Alarmy
 * krytyczne, temperatura i podsumowanie dobowe wychodzą jak dotąd.
 */
export const TRYB_DOMYSLNY: TrybPowiadomien = 'zrownowazony';

export const OPIS_TRYBU: Record<TrybPowiadomien, { nazwa: string; opis: string }> = {
  natychmiast: {
    nazwa: 'Natychmiast',
    opis: 'Każde zdarzenie od razu, także zadanie spóźnione o 0 minut.',
  },
  zrownowazony: {
    nazwa: 'Zrównoważony',
    opis: 'Zadanie po terminie dopiero od 15 minut spóźnienia. Pozostałe bez zmian.',
  },
  tylko_wazne: {
    nazwa: 'Tylko ważne',
    opis:
      'Wyłącznie alarmy krytyczne (HACCP, bezpieczeństwo, awarie) i zadania spóźnione ponad godzinę. ' +
      'Temperatura tylko po przekroczeniu progu krytycznego.',
  },
  wlasny: {
    nazwa: 'Własny',
    opis: 'Każde zdarzenie ustawiasz osobno.',
  },
};

/** Warunek, który dane zdarzenie umie sprawdzić na swoim ładunku. */
export type WarunekZdarzenia = 'od_minut_spoznienia' | 'tylko_krytyczne';

export interface ZdarzeniePowiadomienia {
  /** `moduł.zdarzenie` — dokładnie klucz z `mapa.ts`. */
  zdarzenie: string;
  /** Organ, w którego ustawieniach zdarzenie się pokazuje. */
  organ: string;
  /** Nazwa dla człowieka. */
  nazwa: string;
  /**
   * Alarm, który ma docierać zawsze (decyzja D4): żaden tryb gotowy go nie
   * wyłącza, a w trybie „Własny" wyłączenie wymaga potwierdzenia na ekranie.
   */
  krytyczne: boolean;
  /**
   * `false` = mail systemowy (program partnerski, kontrola platformy) — nie
   * należy do ustawień organizacji i zawsze jest wysyłany.
   */
  konfigurowalne: boolean;
  warunek?: WarunekZdarzenia;
}

/** Zdarzenie podsumowania dobowego — wysyłane przez scheduler, nie przez mapę. */
export const ZDARZENIE_PODSUMOWANIA_DOBOWEGO = 'neurosense.podsumowanie_dobowe';

export const NAZWY_ORGANOW_POWIADOMIEN: Record<string, string> = {
  neurosense: 'NeuroSense',
  waterandice: 'Temperatio',
  fabrica: 'Fabrica',
  print: 'Cloud Print',
  provisio: 'Provisio',
  sprzedaz: 'Sprzedaż',
  nexus_eye: 'Nexus Eye',
  agenda: 'Agenda',
  lexshift: 'LexShift',
  gamification: 'Aegis',
  artifex: 'Artifex',
  nexus: 'Nexus',
  platforma: 'Platforma',
};

function z(
  zdarzenie: string,
  nazwa: string,
  opcje: { krytyczne?: boolean; konfigurowalne?: boolean; warunek?: WarunekZdarzenia } = {}
): ZdarzeniePowiadomienia {
  return {
    zdarzenie,
    organ: zdarzenie.split('.')[0],
    nazwa,
    krytyczne: opcje.krytyczne ?? false,
    konfigurowalne: opcje.konfigurowalne ?? true,
    ...(opcje.warunek ? { warunek: opcje.warunek } : {}),
  };
}

/**
 * Katalog. Komplet względem `mapa.ts` (w obie strony) pilnuje test — zdarzenie
 * dodane do mapy bez wpisu tutaj nie przejdzie CI.
 */
export const KATALOG_POWIADOMIEN: readonly ZdarzeniePowiadomienia[] = [
  z('neurosense.sensor_alert', 'Alert czujnika'),
  z('neurosense.sensor_offline', 'Czujnik nie odpowiada'),
  z('neurosense.temperature_threshold_breach', 'Temperatura poza normą (HACCP)', {
    krytyczne: true,
    warunek: 'tylko_krytyczne',
  }),
  z('neurosense.ph_threshold_breach', 'pH poza normą (HACCP)', { krytyczne: true }),
  z('neurosense.humidity_threshold_breach', 'Wilgotność poza normą (HACCP)', { krytyczne: true }),
  z('neurosense.battery_low', 'Słaba bateria czujnika'),
  z('neurosense.alarm_bezpieczenstwa', 'Alarm bezpieczeństwa', { krytyczne: true }),
  z(ZDARZENIE_PODSUMOWANIA_DOBOWEGO, 'Podsumowanie dobowe'),
  z('waterandice.temperature_alert', 'Temperatura wody poza normą (HACCP)', { krytyczne: true }),
  z('fabrica.production_order_completed', 'Zlecenie produkcyjne zakończone'),
  z('fabrica.quality_check_failed', 'Kontrola jakości niezaliczona', { krytyczne: true }),
  z('fabrica.sourdough_feeding_overdue', 'Zakwas do dokarmienia'),
  z('fabrica.material_shortage', 'Brak surowca'),
  z('fabrica.equipment_alarm', 'Alarm urządzenia', { krytyczne: true }),
  z('fabrica.panel_fault', 'Usterka stanowiska', { krytyczne: true }),
  z('fabrica.calibration_due', 'Wzorcowanie wagi do wykonania'),
  z('print.failed', 'Wydruk nieudany'),
  z('provisio.low_stock_alert', 'Niski stan magazynowy'),
  z('provisio.out_of_stock_alert', 'Brak na stanie'),
  z('sprzedaz.zastoj', 'Zastój sprzedaży'),
  z('nexus_eye.gateway_milczy', 'Bramka kamer milczy', { krytyczne: true }),
  z('agenda.task_assigned', 'Przypisano zadanie'),
  z('agenda.task_overdue', 'Zadanie po terminie', { warunek: 'od_minut_spoznienia' }),
  z('lexshift.leave_requested', 'Wniosek urlopowy do akceptacji'),
  z('lexshift.leave_approved', 'Urlop zaakceptowany'),
  z('lexshift.ewidencja_niewyslana', 'Ewidencja czasu niewysłana'),
  z('lexshift.schedule_published', 'Opublikowano grafik'),
  z('gamification.achievement_earned', 'Zdobyta odznaka'),
  z('artifex.policy_admitted', 'Polityka AI dopuszczona'),
  z('artifex.consent_withdrawn', 'Wycofana zgoda'),
  z('nexus.budget_threshold_reached', 'Budżet Nexusa przekroczony'),
  z('nexus.policy_violation', 'Naruszenie polityki Nexusa', { krytyczne: true }),
  z('nexus.edukacja_zaproszenie', 'Zaproszenie do edukacji partnera', { konfigurowalne: false }),
  z('nexus.szkolenie_zaproszenie', 'Zaproszenie na szkolenie partnera', { konfigurowalne: false }),
  z('nexus.certyfikat_wygasa', 'Certyfikat partnera wygasa', { konfigurowalne: false }),
  z('platforma.kontrola_licencjonowania', 'Kontrola licencjonowania platformy', {
    konfigurowalne: false,
  }),
];

const PO_ZDARZENIU = new Map(KATALOG_POWIADOMIEN.map((w) => [w.zdarzenie, w]));

export function wpisKatalogu(zdarzenie: string): ZdarzeniePowiadomienia | null {
  return PO_ZDARZENIU.get(zdarzenie) ?? null;
}

// ---------------------------------------------------------------------------
// Wiersze tabeli `reguly_powiadomien`
// ---------------------------------------------------------------------------

/** Klucz wiersza trybu organu: `<organ>.*`. */
export function kluczTrybu(organ: string): string {
  return `${organ}.*`;
}

/** Wiersz `public.reguly_powiadomien` (kanał e-mail). */
export interface WierszRegulyPowiadomien {
  klucz: string;
  wlaczone: boolean;
  parametry: Record<string, unknown>;
}

/** Ustawienie pojedynczego zdarzenia w trybie „Własny". */
export interface UstawienieZdarzenia {
  wlaczone: boolean;
  /** `null` = okno tłumienia z `mapa.ts`. */
  powtorzCoMin: number | null;
  odMinutSpoznienia: number;
  tylkoKrytyczne: boolean;
}

/** Wynik: co nadawca ma zrobić z tym zdarzeniem w tej organizacji. */
export interface RegulaEfektywna extends UstawienieZdarzenia {
  tryb: TrybPowiadomien;
}

/** Granice parametrów — te same co więzy CHECK w migracji. */
export const MAKS_MINUT_SPOZNIENIA = 1440;
export const MAKS_POWTORZ_CO_MIN = 10080;

function ustawienieTrybu(
  tryb: Exclude<TrybPowiadomien, 'wlasny'>,
  wpis: ZdarzeniePowiadomienia
): UstawienieZdarzenia {
  switch (tryb) {
    case 'natychmiast':
      return { wlaczone: true, powtorzCoMin: null, odMinutSpoznienia: 0, tylkoKrytyczne: false };
    case 'zrownowazony':
      return { wlaczone: true, powtorzCoMin: null, odMinutSpoznienia: 15, tylkoKrytyczne: false };
    case 'tylko_wazne':
      return {
        wlaczone: wpis.krytyczne || wpis.warunek === 'od_minut_spoznienia',
        powtorzCoMin: null,
        odMinutSpoznienia: 60,
        tylkoKrytyczne: true,
      };
  }
}

function liczbaZZakresu(v: unknown, maks: number): number | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  const n = Math.round(v);
  return n >= 0 && n <= maks ? n : null;
}

/** Tryb organu odczytany z wierszy; nieznana wartość = tryb domyślny. */
export function trybOrganu(
  organ: string,
  wiersze: readonly WierszRegulyPowiadomien[]
): TrybPowiadomien {
  const w = wiersze.find((x) => x.klucz === kluczTrybu(organ));
  const t = w?.parametry?.tryb;
  return typeof t === 'string' && (TRYBY_POWIADOMIEN as readonly string[]).includes(t)
    ? (t as TrybPowiadomien)
    : TRYB_DOMYSLNY;
}

/**
 * Reguła, którą nadawca stosuje do zdarzenia w organizacji.
 *
 * Zdarzenie spoza katalogu i zdarzenie niekonfigurowalne → wysyłka jak
 * dotąd (`natychmiast`). Tryb gotowy liczy się z katalogu; „Własny" bierze
 * wiersz zdarzenia, a brakujące pola uzupełnia wartościami trybu
 * „Natychmiast" — wiersz, którego nikt nie zapisał, nie może niczego wyciszyć.
 */
export function regulaEfektywna(
  zdarzenie: string,
  wiersze: readonly WierszRegulyPowiadomien[]
): RegulaEfektywna {
  const wpis = wpisKatalogu(zdarzenie);
  if (!wpis || !wpis.konfigurowalne) {
    return {
      tryb: 'natychmiast',
      wlaczone: true,
      powtorzCoMin: null,
      odMinutSpoznienia: 0,
      tylkoKrytyczne: false,
    };
  }
  const tryb = trybOrganu(wpis.organ, wiersze);
  if (tryb !== 'wlasny') return { tryb, ...ustawienieTrybu(tryb, wpis) };

  const baza = ustawienieTrybu('natychmiast', wpis);
  const w = wiersze.find((x) => x.klucz === zdarzenie);
  if (!w) return { tryb, ...baza };
  const p = w.parametry ?? {};
  return {
    tryb,
    wlaczone: w.wlaczone,
    powtorzCoMin: liczbaZZakresu(p.powtorz_co_min, MAKS_POWTORZ_CO_MIN),
    odMinutSpoznienia:
      liczbaZZakresu(p.od_minut_spoznienia, MAKS_MINUT_SPOZNIENIA) ?? baza.odMinutSpoznienia,
    tylkoKrytyczne: p.tylko_krytyczne === true,
  };
}

/** Konfigurowalne zdarzenia organu, w kolejności katalogu. */
export function zdarzeniaOrganu(organ: string): ZdarzeniePowiadomienia[] {
  return KATALOG_POWIADOMIEN.filter((w) => w.organ === organ && w.konfigurowalne);
}

/** Organy z co najmniej jednym konfigurowalnym zdarzeniem, w kolejności katalogu. */
export function organyPowiadomien(): string[] {
  // Bez spreadu `Set`: ten plik kompilują też aplikacje z celem ES5
  // (`module-agenda`), a tam iteracja `Set` wymaga `downlevelIteration`.
  const organy: string[] = [];
  for (const w of KATALOG_POWIADOMIEN) {
    if (w.konfigurowalne && !organy.includes(w.organ)) organy.push(w.organ);
  }
  return organy;
}

/** Wiersz zdarzenia (tryb „Własny") z ustawienia — kształt kolumny `parametry`. */
export function wierszZdarzenia(
  zdarzenie: string,
  u: UstawienieZdarzenia
): WierszRegulyPowiadomien {
  const warunek = wpisKatalogu(zdarzenie)?.warunek;
  const parametry: Record<string, unknown> = {};
  if (u.powtorzCoMin !== null) parametry.powtorz_co_min = u.powtorzCoMin;
  if (warunek === 'od_minut_spoznienia') parametry.od_minut_spoznienia = u.odMinutSpoznienia;
  if (warunek === 'tylko_krytyczne') parametry.tylko_krytyczne = u.tylkoKrytyczne;
  return { klucz: zdarzenie, wlaczone: u.wlaczone, parametry };
}

/**
 * Wiersze, którymi startuje tryb „Własny": DOKŁADNIE skutek trybu, z którego
 * organizacja przechodzi. Bez tego przejście z „Zrównoważonego" na „Własny"
 * cicho wracałoby do maili od 0 minut — ekran pokazywałby 15, a nadawca
 * liczyłby z braku wiersza.
 */
export function wierszeStartuTrybuWlasnego(
  organ: string,
  wiersze: readonly WierszRegulyPowiadomien[]
): WierszRegulyPowiadomien[] {
  return zdarzeniaOrganu(organ).map((w) =>
    wierszZdarzenia(w.zdarzenie, regulaEfektywna(w.zdarzenie, wiersze))
  );
}

export type DecyzjaWysylki =
  | { wysylac: true }
  | { wysylac: false; powod: 'wylaczone' | 'ponizej_progu_spoznienia' | 'nie_krytyczne' };

/**
 * Czy zdarzenie z TYM ładunkiem spełnia regułę. Warunki działają wyłącznie
 * na zdarzeniach, które je deklarują w katalogu.
 *
 * Spóźnienie poniżej progu NIE gubi maila: Agenda publikuje `task_overdue`
 * przy każdym tyknięciu z rosnącym `minutes_overdue`, więc mail wyjdzie przy
 * pierwszym tyknięciu po progu. Brak pola w ładunku = wysyłamy (zgubiony mail
 * jest gorszy niż mail za wcześnie).
 */
export function decyzjaWysylki(
  zdarzenie: string,
  regula: RegulaEfektywna,
  ladunek: Record<string, unknown>
): DecyzjaWysylki {
  if (!regula.wlaczone) return { wysylac: false, powod: 'wylaczone' };
  const warunek = wpisKatalogu(zdarzenie)?.warunek;

  if (warunek === 'od_minut_spoznienia' && regula.odMinutSpoznienia > 0) {
    const minuty = Number(ladunek.minutes_overdue);
    if (Number.isFinite(minuty) && minuty < regula.odMinutSpoznienia) {
      return { wysylac: false, powod: 'ponizej_progu_spoznienia' };
    }
  }

  if (warunek === 'tylko_krytyczne' && regula.tylkoKrytyczne) {
    const rodzaj = ladunek.alert_type ?? ladunek.alertType;
    if (typeof rodzaj === 'string' && rodzaj !== 'critical') {
      return { wysylac: false, powod: 'nie_krytyczne' };
    }
  }

  return { wysylac: true };
}
